/* eslint-disable @tencent/i18next/no-literal-string */
import {
    FontSizeSimplyResult,
    IFontSizeResult,
} from '@src/slide/application/service/slide-rating-and-improve/text/text-analyzer.interface';
import {
    AnalyzerResultDescriptionAndToSelectModel,
    AnalyzerResultType,
    ITextElementBoxNode,
} from '../../slide-content-analyzer.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import {
    setTextRunStyles,
} from '@src/slide/domain/ui-model/element-modeles/property/text/text-operation';
import { transaction as sdkTransaction } from '@src/slide/domain/slide-app';
import {
    isStringVisible,
} from '@src/slide/application/service/slide-rating-and-improve/text/calculate/text-box-calculate';
import i18n from 'i18next';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

export enum FontSizeType {
    Title,
    SubTitle,
}

export class FontSizeResult implements IFontSizeResult {
    public analyzerName: string;
    public description: string;
    public resultInfo: Array<ITextElementBoxNode>;
    public resultType: AnalyzerResultType;
    public readonly minSize: number;

    private simplifyResultInfo: Array<FontSizeSimplyResult> = [];
    private showResult: Array<AnalyzerResultDescriptionAndToSelectModel> = [];
    public constructor(analyzerName: string, description: string, minSize: number, private type: FontSizeType) {
        this.analyzerName = analyzerName;
        this.description = description;
        this.reset();
        this.minSize = minSize;
    }

    public reset() {
        this.resultType = AnalyzerResultType.SUCCESS;
        this.resultInfo = [];
        this.simplifyResultInfo = [];
        this.showResult = [];
    }

    public onPresentationEnd() {
        this.simplifyResult();
    }

    public getShowResult(): Array<AnalyzerResultDescriptionAndToSelectModel> {
        return this.showResult;
    }

    public getDescriptionWithSelect(): Array<AnalyzerResultDescriptionAndToSelectModel> | undefined {
        if (this.resultType === AnalyzerResultType.SUCCESS) return undefined;
        if (this.showResult.length > 0) return this.showResult;
        const result: Array<AnalyzerResultDescriptionAndToSelectModel> = [];
        for (let i = 0; i < this.simplifyResultInfo.length; i++) {
            const current = this.simplifyResultInfo[i];
            const start = this.resultInfo[current.startIndex];
            const description = i18n.t(
                '第{0}页的 \'{1}\' 字号小于{2}',
                { 0: start.slideNode.slideIndex + 1, 1: current.text, 2: this.minSize },
            );
            result.push({
                description,
                selectable: true,
                selectSlide: start.slideNode.slide,
                selectElement: start.element,
                handleProblem: this.handleItemClick,
            });
        }
        this.showResult = result;
        return result;
    }

    /**
     * 合并同一段落的结果
     * @private
     */
    private simplifyResult() {
        const showResult: Array<FontSizeSimplyResult> = [];
        let lastResult: ITextElementBoxNode | undefined = undefined;
        let lastStartIndex = 0;
        let lastString = '';
        let firstHasTextIndex = -1;
        // eslint-disable-next-line complexity
        this.resultInfo.forEach((item, index) => {
            if (lastResult
                && lastResult.textBox.parentBox === item.textBox.parentBox
                && lastResult.textBox.nextSibling === item.textBox
            ) { // 和上一个textBox连续，则合并记录
                lastResult = item;
                lastString += item.textBox.text;
                // 最后一个，该记录了
                if (index === this.resultInfo.length - 1) {
                    showResult.push({
                        text: lastString,
                        startIndex: lastStartIndex,
                        endIndex: index,
                    });
                }
            } else {
                if (index > 0 && lastResult) {
                    // 已经存在连续的结果，则记录
                    showResult.push({
                        text: lastString,
                        startIndex: lastStartIndex,
                        endIndex: index - 1,
                    });
                }
                if (isStringVisible(item.textBox.text) || firstHasTextIndex === -1) {
                    firstHasTextIndex = index;
                }
                if (firstHasTextIndex !== -1) {
                    lastStartIndex = index;
                    lastResult = item;
                    lastString = item.textBox.text;
                    // 最后一个，也该记录当前结果
                    if (index === this.resultInfo.length - 1) {
                        showResult.push({
                            text: lastString,
                            startIndex: lastStartIndex,
                            endIndex: index,
                        });
                    }
                }
            }
        });
        this.simplifyResultInfo = showResult;
        if (showResult.length === 0) {
            this.resultType = AnalyzerResultType.SUCCESS;
        }
    }

    private handleItemClick = (index: number) => {
        const currentShowResult = this.simplifyResultInfo[index];
        if (this.showResult[index].selectSlide?.sdkData
            && this.showResult[index].selectElement?.sdkData) {
            sdkTransaction(() => {
                const paths: Array<Array<number>> = [];
                for (let i = currentShowResult.startIndex; i <= currentShowResult.endIndex; i++) {
                    const currentItem = this.resultInfo[i];
                    const path = this.prepareParagraphListPath(currentItem);
                    if (path) {
                        paths.push(path);
                    }
                }
                const element: IShapeModel = this.resultInfo[currentShowResult.startIndex].element as IShapeModel;
                if (!element?.paragraphList) return;
                const newPara = setTextRunStyles(element.paragraphList, { fontSize: this.minSize }, paths);
                if (newPara.length > 0) {
                    element.updateParagraphList(newPara);
                }
            });
            let action = TDWActionType.BEAUTIFY_TITLE_SIZE;
            if (this.type === FontSizeType.SubTitle) {
                action = TDWActionType.BEAUTIFY_SUBTITLE_SIZA;
            }
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.AUTO_BEAUTIFY,
                action,
            });
        }
        this.removeOneResult(index);
    };

    private removeOneResult(index: number) {
        this.simplifyResultInfo.splice(index, 1);
        this.showResult.splice(index, 1);
        if (this.showResult.length === 0) this.resultType = AnalyzerResultType.SUCCESS;
    }

    private prepareParagraphListPath(currentItem: ITextElementBoxNode): Array<number> | undefined {
        // @ts-ignore
        const documentElementPath: Array<number> | undefined = currentItem.textBox.textStyle?.getCustomizeProp?.('slatePath');
        return documentElementPath;
    }
}
